// ============================================================================
// REPORT THEMES — single source of truth for report branding.
//
// Two themes:
//   'servicenow' — "Petrol & Green": deep petrol-teal dominant, ServiceNow
//                  green accent. (Default.)
//   'atlassian'  — "Navy & Blue": Atlassian Design System colours, Jira-blue
//                  accent.
//
// Each theme carries:
//   label       — display name for pickers
//   brandSuffix — wordmark suffix ("Process Advisor for <suffix>")
//   pptx        — palette for consultingReport-pptx.js (hex WITHOUT '#')
//   html        — palette for consultingReport-renderer.js (hex WITH '#')
//
// The pptx and html key sets intentionally mirror the existing C and P palette
// objects in those files, so a theme swap is a straight Object.assign.
// ============================================================================

export const DEFAULT_REPORT_THEME = 'servicenow';

export const REPORT_THEMES = {
  // ── ServiceNow · "Petrol & Green" ─────────────────────────────────────────
  servicenow: {
    id: 'servicenow',
    label: 'ServiceNow',
    brandSuffix: 'for ServiceNow',
    pptx: {
      primary:      '0E7C8C',  // Saturated teal (kickers, labels, medium priority)
      navy:         '0C2F3F',  // Deep petrol (headings)
      primaryDark:  '0A4A57',  // Dark petrol (table header bands, dark nodes)
      accent:       '0FA0A6',  // Bright teal (bar fills)
      snGreen:      '00A13A',  // Brand accent on light backgrounds
      snGreenBright:'3DD37A',  // Brand accent lifted for dark cover background
      danger:       'B42318',
      dangerBg:     'FEF3F2',
      dangerBorder: 'FECDCA',
      warn:         'B54708',
      warnBg:       'FFFAEB',
      warnBorder:   'FEDF89',
      success:      '067647',
      successBg:    'ECFDF3',
      successBorder:'ABEFC6',
      ink:          '122A35',
      text:         '41525F',
      muted:        '6A7A86',
      card:         'FAFBFB',
      tint:         'E9F5F4',
      tintBorder:   'A9CFCF',
      barTrack:     'E6F1F1',
      border:       'E5E9EB',
      white:        'FFFFFF',
      scoreBg:      'E7F3F3',
      dotGrid:      'CBE3E1',
      dotGridDark:  '2E5D6B',
      coverLight:   'C6DEE2',
      coverMuted:   '9CBFC7',
    },
    html: {
      primary:          '#0E7C8C',
      primaryDark:      '#0A4A57',
      primaryAlt:       '#2596A6',
      accent:           '#0FA0A6',
      accentDark:       '#0A6A70',
      accentTint:       '#E4F5F6',
      danger:           '#D92D20',
      dangerDark:       '#B42318',
      dangerBg:         '#FEE4E2',
      dangerTint:       '#FEF3F2',
      dangerBorder:     '#FDA29B',
      dangerBorderSoft: '#FECDCA',
      warn:             '#DC6803',
      warnDark:         '#B54708',
      warnBg:           '#FEF0C7',
      warnBorder:       '#FEDF89',
      warnTint:         '#FFFAEB',
      warnTintAlt:      '#FFFCF5',
      warnAlt:          '#F79009',
      success:          '#067647',
      successBg:        '#ECFDF3',
      successBorder:    '#ABEFC6',
      purple:           '#5E548E',
      ink:              '#122A35',
      text:             '#41525F',
      muted:            '#6A7A86',
      mutedAlt:         '#5C6E7A',
      pageBg:           '#F3F6F6',
      card:             '#FAFBFB',
      cardAlt:          '#F5F9F9',
      cardAlt2:         '#F7FAFA',
      border:           '#E7EBEB',
      borderAlt:        '#E5E9EB',
      borderStrong:     '#DAE2E2',
      borderFaint:      '#F0F3F3',
      tint:             '#E9F5F4',
      tintDeep:         '#DCEEED',
      tintBg:           '#F0F8F7',
      tintBg2:          '#F5FBFA',
      tintBorder:       '#A9CFCF',
      tintBorderStrong: '#7FB5B5',
    },
  },

  // ── Atlassian · "Navy & Blue" (Atlassian Design System colours) ───────────
  atlassian: {
    id: 'atlassian',
    label: 'Atlassian',
    brandSuffix: 'for Jira',
    pptx: {
      primary:      '0052CC',  // Jira brand blue (kickers, labels, medium priority)
      navy:         '172B4D',  // ADS dark neutral (headings)
      primaryDark:  '09326C',  // ADS B900 (table header bands, dark nodes)
      accent:       '1D7AFC',  // Bright blue (bar fills)
      snGreen:      '1D7AFC',  // Brand accent on light backgrounds (accent lines, wordmark suffix)
      snGreenBright:'579DFF',  // Brand accent lifted for dark cover background
      danger:       'C9372C',
      dangerBg:     'FFECEB',
      dangerBorder: 'FD9891',
      warn:         'A54800',
      warnBg:       'FFF3EB',
      warnBorder:   'FEDEC8',
      success:      '216E4E',
      successBg:    'DCFFF1',
      successBorder:'BAF3DB',
      ink:          '172B4D',
      text:         '44546F',
      muted:        '626F86',
      card:         'FAFBFC',
      tint:         'E9F2FF',
      tintBorder:   'A6C5F2',
      barTrack:     'E8EEF6',
      border:       'E1E5EA',
      white:        'FFFFFF',
      scoreBg:      'E9F2FF',
      dotGrid:      'CFE0F8',
      dotGridDark:  '2E4E7E',
      coverLight:   'C8D9F4',
      coverMuted:   '8FA7CC',
    },
    html: {
      primary:          '#0052CC',
      primaryDark:      '#09326C',
      primaryAlt:       '#1D7AFC',
      accent:           '#2898BD',
      accentDark:       '#206A83',
      accentTint:       '#E7F9FF',
      danger:           '#C9372C',
      dangerDark:       '#AE2A19',
      dangerBg:         '#FFECEB',
      dangerTint:       '#FFF5F4',
      dangerBorder:     '#F87168',
      dangerBorderSoft: '#FD9891',
      warn:             '#C25100',
      warnDark:         '#A54800',
      warnBg:           '#FEDEC8',
      warnBorder:       '#FEC57B',
      warnTint:         '#FFF3EB',
      warnTintAlt:      '#FFF8F3',
      warnAlt:          '#E56910',
      success:          '#216E4E',
      successBg:        '#DCFFF1',
      successBorder:    '#BAF3DB',
      purple:           '#5E4DB2',
      ink:              '#172B4D',
      text:             '#44546F',
      muted:            '#626F86',
      mutedAlt:         '#596F8C',
      pageBg:           '#F4F5F7',
      card:             '#FAFBFC',
      cardAlt:          '#F7F9FC',
      cardAlt2:         '#F8FAFD',
      border:           '#E6E9EF',
      borderAlt:        '#E1E5EA',
      borderStrong:     '#D5DBE3',
      borderFaint:      '#F0F2F6',
      tint:             '#E9F2FF',
      tintDeep:         '#DEEBFF',
      tintBg:           '#F0F6FF',
      tintBg2:          '#F6FAFF',
      tintBorder:       '#A6C5F2',
      tintBorderStrong: '#7EA6E8',
    },
  },
};

// Options list for pickers, in display order.
export const REPORT_THEME_OPTIONS = [
  { id: 'servicenow', label: 'ServiceNow' },
  { id: 'atlassian',  label: 'Atlassian' },
];

export function getReportTheme(themeId) {
  return REPORT_THEMES[themeId] || REPORT_THEMES[DEFAULT_REPORT_THEME];
}

// Maps a platform string ('ServiceNow' | 'Jira') to a theme id, so existing
// callers that only pass opts.platform keep working.
export function themeFromPlatform(platform) {
  return /jira|atlassian|forge/i.test(String(platform || '')) ? 'atlassian' : 'servicenow';
}

export default REPORT_THEMES;
